import React from "react";
import { PlayIcon } from "lucide-react";
import { BaseModelCandidate } from "../../services/BaseModelCandidateService";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import { Button } from "../ui/button";

const STATUS_VARIANT: Record<string, string> = {
  PENDING: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
  DISCOVERING:
    "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/20",
  CONFIG_READY:
    "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/20",
  READY: "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20",
  FAILED: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/20",
};

interface BaseModelCandidatesTableProps {
  candidates: BaseModelCandidate[];
  loading: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalItems: number;
  discoveringId: number | null;
  onDiscover: (c: BaseModelCandidate) => void;
}

const BaseModelCandidatesTable: React.FC<BaseModelCandidatesTableProps> = ({
  candidates,
  loading,
  currentPage,
  onPageChange,
  pageSize,
  totalItems,
  discoveringId,
  onDiscover,
}) => {
  const columns: PaginatedTableColumn<BaseModelCandidate>[] = [
    {
      key: "id",
      header: "ID",
      width: "w-16",
      render: (c) => (
        <span className="font-mono text-xs">{c.id}</span>
      ),
    },
    {
      key: "hf_model_id",
      header: "HF Model ID",
      width: "w-[25%]",
      render: (c) => (
        <span className="font-mono text-sm">{c.hf_model_id}</span>
      ),
    },
    {
      key: "display_name",
      header: "Display Name",
      width: "w-[20%]",
      render: (c) => (
        <span className="text-sm">{c.display_name || "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-28",
      render: (c) => (
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
            STATUS_VARIANT[c.status] || "bg-muted text-muted-foreground"
          }`}
        >
          {c.status}
        </span>
      ),
    },
    {
      key: "inferred_family",
      header: "Family",
      width: "w-28",
      render: (c) => (
        <span className="text-sm text-muted-foreground">
          {c.inferred_family || "—"}
        </span>
      ),
    },
    {
      key: "date_created",
      header: "Created",
      width: "w-36",
      render: (c) => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {c.date_created
            ? new Date(c.date_created).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })
            : "—"}
        </span>
      ),
    },
    {
      key: "id",
      header: "Actions",
      width: "w-28",
      render: (c) => (
        <>
          {(c.status === "PENDING" || c.status === "FAILED") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDiscover(c);
              }}
              disabled={discoveringId === c.id}
            >
              <PlayIcon
                className={`w-4 h-4 mr-1 ${
                  discoveringId === c.id ? "animate-spin" : ""
                }`}
              />
              {discoveringId === c.id ? "Discovering..." : "Discover"}
            </Button>
          )}
        </>
      ),
    },
  ];

  return (
    <PaginatedTable
      data={candidates}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      totalItems={totalItems}
      serverSidePagination={true}
      showPagination={true}
      emptyTitle="No candidates found"
      emptyDescription="Add a HuggingFace model to get started, or try a different search."
    />
  );
};

export default BaseModelCandidatesTable;
